import { useEffect, useState } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';

import { useRequest } from '@frontend/api';
import type { QueryFunction } from '@frontend/api/src/common/queryFunction';

import { getStockRecommendationQueryKey } from './keys';
import type {
  StockRecommendationRequestBody,
  StockRecommendationResponseBody,
  UseStockRecommendationQueryKey,
  UseStockRecommendationHookResponse,
} from './types';

export const useStockRecommendationQuery: QueryFunction<
  StockRecommendationResponseBody,
  [StockRecommendationRequestBody],
  UseStockRecommendationQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  const queryKey = getStockRecommendationQueryKey(params);
  return useQuery<
    StockRecommendationResponseBody,
    unknown,
    StockRecommendationResponseBody,
    UseStockRecommendationQueryKey
  >({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(`/materials/stock_recommendation/${params.partFileUuid}`, {
        init: {
          method: 'POST',
          body: { partUuid: params.partUuid },
          signal,
        },
      });
    },
  });
};

/**
 * Wraps around a stock recommendation query. The query polls until the recommendation is ready, or until error
 * @param partFileUuid The part file UUID
 * @param partUuid The part UUID
 * @param queryOptions Additional query options
 * @returns A response object, or null
 */
export const useStockRecommendation = (
  partFileUuid: string,
  partUuid: string,
  onPollTimeout: () => void | undefined,
  queryOptions: UseQueryOptions = {},
): UseStockRecommendationHookResponse => {
  const [stockRecommendation, setStockRecommendation] =
    useState<StockRecommendationResponseBody | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [isFetchingInitial, setIsFetchingInitial] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    setIsPolling(true);
  }, [setIsPolling]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (isPolling) {
      timeoutId = setTimeout(() => {
        setIsPolling(false);
        if (onPollTimeout) {
          onPollTimeout();
        }
      }, 1000 * 30); // stop polling after 30 seconds
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPolling, setIsPolling, onPollTimeout]);

  useStockRecommendationQuery(
    { partFileUuid, partUuid },
    {
      queryOptions: {
        refetchInterval: isPolling ? 5000 : 0,
        ...queryOptions,
        onSuccess: (response) => {
          setStockRecommendation(response);
          setIsFetchingInitial(false);
          setIsError(false);
          if (response.recommendationReady) {
            setIsPolling(false);
          }
          if (queryOptions.onSuccess) {
            queryOptions.onSuccess(response);
          }
        },
        onError: (error) => {
          setIsPolling(false);
          setIsFetchingInitial(false);
          setIsError(true);
          if (queryOptions.onError) {
            queryOptions.onError(error);
          }
        },
      },
    },
  );

  return {
    stockRecommendation,
    isFetchingInitial,
    isError,
    isPolling,
  };
};
